import { MetaMaskInpageProvider } from '@metamask/providers'
//import { Maybe } from '@metamask/providers';
/**
 * Class(es) to interact with Metamask
 */
export class EthereumChain {
	constructor(
		public chainId: ChainId, // A 0x-prefixed hexadecimal string
		public chainName: string,
		public rpcUrls: string[],
		public blockExplorerUrls?: string[],
		public nativeCurrency?: Token,
		public iconUrls?: string[] // Currently ignored.
	) {
	}
}

export class Token {
	constructor(
		public symbol: string, // A ticker symbol or shorthand, up to 5 chars.
		public decimals: number = 18, // The number of decimals in the token
		public address?: string, // The address that the token is at.
		public image?: string // A string url of the token logo
	) {
	}

	public static MATIC: Token = new Token('MATIC')
}

/**A 0x-prefixed hexadecimal string
/// See [chainId.network]{@link https://chainid.network} for more information.
 */
export enum ChainId {
	Ropsten = '0x3',
	Rinkeby = '0x4', // 	Rinkeby Test Network
	Goerli = '0x5', // 	Goerli Test Network
	Kovan = '0x2a', // 	42 	Kovan Test Network
	BSC = '0x38',   // Live Binance Smart Chain
	BSCTest = '0x61',  // 97
	Polygon = '0x89', // Polygon Mainnet
	Mumbai = '0x13881' // Polygon test net
}

export const Chain = {
	x89: new EthereumChain(
		ChainId.Polygon,
		'Polygon Mainnet',
		['https://polygon-rpc.com'], // https://rpc.ankr.com/polygon, https://rpc.maticvigil.com/
		['https://polygonscan.com'],
		Token.MATIC
	),
	x13881: new EthereumChain(
		ChainId.Mumbai,
		'Mumbai',
		['https://matic-mumbai.chainstacklabs.com','https://rpc-mumbai.maticvigil.com','https://polygon-mumbai.chainstacklabs.com'], //https://rpc.ankr.com/polygon_mumbai
		['https://mumbai.polygonscan.com/'],
		Token.MATIC
	)
}

/**
 * Methods the metamask interface extended with
 */
export interface MetaMask extends MetaMaskInpageProvider {
	requestAccounts(): Promise<string[]>

	/** Add a token to the watched tokens list */
	watchAsset(options: Token): Promise<boolean>

	switchToChain(chainId: ChainId): Promise<any>

	addEthereumChain(params: EthereumChain): Promise<any>

	getBalance(address: string): Promise<BigInt>

	/** The chain ID of the currently connected Ethereum chain. */
	getChainId(): Promise<string>

	addNswitchChain(chainId: ChainId): Promise<any>
}

export class MetaMask {
	/**
	 * Checks whether the MetaMask extension is installed
	 */
	static isInstalled(): boolean {
		return (window as any).ethereum && (window as any).ethereum.isMetaMask;
	}

	static getMetaMask(): MetaMask | undefined {
		if ( MetaMask.isInstalled() ) {
			return MetaMask.mixin((window as any).ethereum as MetaMask);
		}
	}

	static mixin(eth: MetaMask): MetaMask {
		//// Some (most used) standard methods
		eth.requestAccounts = (): Promise<string[]> => {
			return eth.request({method: 'eth_requestAccounts'});
		};

		eth.getBalance = (address: string): Promise<BigInt> => {
			return eth.request({method: 'eth_getBalance', params: [address, 'latest']}) as Promise<BigInt>
		}

		eth.addEthereumChain = (chain: EthereumChain): Promise<any> => {
			return eth.request({method: 'wallet_addEthereumChain', params: [chain]})
		}

		eth.switchToChain = (chainId: ChainId): Promise<any> => 
			eth.request({method: 'wallet_switchEthereumChain', params: [{chainId}]})

		eth.watchAsset = (options: Token): Promise<boolean> =>
			eth.request<boolean>({
				method: 'wallet_watchAsset',
				params: {
					type: 'ERC20', // Initially only supports ERC20, but eventually more!
					options
				}
			})

		/** The chain ID of the currently connected Ethereum chain. */
		eth.getChainId = (): Promise<string> => 
			eth.request<string>({method: 'eth_chainId'})

		eth.addNswitchChain = (chainId: ChainId): Promise<any> => {
			return eth.switchToChain(chainId)
				.catch(err => {
					console.log(err)
					if (err.code && err.code === 4902) {
						const chain = Chain[chainId.substring(1)]
						console.log(`Chain ${chainId} not found adding`, chain)
						return eth.addEthereumChain(chain)
						//.then(() => eth.switchToChain(chainId)) //Try to do the switch again
					} else {
						return Promise.reject(err) // Forward the error
					}
				})
		}
		return eth;
	}
}